import words from 'lodash/words';
import isEmpty from 'lodash/isEmpty';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectUserProjectTimeZone } from 'core/userContext';
import { DisplayUntilTakeover } from 'modules/campaigns/lib/takeovers';
import { Edit16 } from 'ui/icons';
import { colors } from 'ui/theme';
import { TipPlacement } from 'ui/tooltip';
import { useCampaign } from '../../BaseSendEditor.hooks';
import { BodyText, EditButton, EditButtonTooltip, HeaderText, Li, Ul } from './DisplayUntil.styles';

const TOOLTIP_COPY = 'Must first create campaign or choose a template to edit display until';

export const EDIT_DISPLAY_UNTIL_TEST_ID = 'edit-display-until';
export const END_DATE_LIST_ITEM_TEST_ID = 'end-date-list-item';
export const PROPERTY_CHANGE_LIST_ITEM_TEST_ID = 'property-change-list-item';

export interface DisplayUntilProps {
  /** The campaign ID from node data */
  campaignId?: number;
  /** Is the edit button disabled */
  isDisabled: boolean;
}

const DisplayUntil = ({ campaignId, isDisabled }: DisplayUntilProps) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const { updateCampaign, campaign } = useCampaign();
  const [isDisplayUntilOpen, setIsDisplayUntilOpen] = useState(false);
  const endDate = campaign?.displayUntil?.endDate;
  const query = campaign?.displayUntil?.searchQuery?.searchQueries?.[0];
  const readableEndDate = useMemo(
    () =>
      endDate
        ? new Intl.DateTimeFormat(undefined, {
            timeZone: projectTimeZone,
            dateStyle: 'full',
            timeStyle: 'short',
          }).format(new Date(endDate as string))
        : '',
    [projectTimeZone, endDate],
  );
  const queryText = useMemo(() => {
    if (!query) return '';
    // There is a lot of weirdness due to using different types between
    // `modules/workflows` and `modules/domains`.  Workflows is more accurate,
    // so domains should be updated with it and the types in modules/workflows
    // should be removed.
    // @ts-ignore
    const { field, value, comparatorType = '' } = query;
    // PascalCased comparators split into separate lowercase words
    // EG: 'IsSet' to 'is set'
    const comparator = words(comparatorType)
      .map((c) => c.toLocaleLowerCase())
      .join(' ');
    return (
      <>
        <code>{field}</code> {comparator} {!isEmpty(value) && <code>{value}</code>}
      </>
    );
  }, [query]);

  return (
    <>
      <EditButtonTooltip
        isEnabled={!campaignId}
        placement={TipPlacement.Left}
        tooltipContent={TOOLTIP_COPY}
      >
        <EditButton
          data-test={EDIT_DISPLAY_UNTIL_TEST_ID}
          onClick={() => setIsDisplayUntilOpen(true)}
          isDisabled={!campaignId || isDisabled}
        >
          <Edit16 color={colors.grey50} isColorInherited={false} />
          Edit
        </EditButton>
      </EditButtonTooltip>
      <HeaderText>Display until</HeaderText>
      <BodyText>This message will display until any of the following occurs.</BodyText>
      <Ul>
        <Li>Journey is off or archived</Li>
        {endDate && <Li data-test={END_DATE_LIST_ITEM_TEST_ID}>Date is {readableEndDate}</Li>}
        {query && <Li data-test={PROPERTY_CHANGE_LIST_ITEM_TEST_ID}>{queryText}</Li>}
      </Ul>

      {isDisplayUntilOpen && (
        <DisplayUntilTakeover
          displayUntil={campaign?.displayUntil}
          onClose={() => setIsDisplayUntilOpen(false)}
          onSave={(displayUntil) => {
            updateCampaign({ displayUntil });
            setIsDisplayUntilOpen(false);
          }}
        />
      )}
    </>
  );
};

export default DisplayUntil;
